import { alpha } from '@mui/material';
import brandclub_colors from '../colors/brandclubColors.js';
import { SMALL_MOBILE_SCREEN_SIZE, MOBILE_SCREEN_SIZE, TABLET_SCREEN_SIZE, DESKTOP_SCREEN_SIZE, XL_DESKTOP_SCREEN_SIZE } from '../utils/constants/Constants.js';

const getDefaultThemePalette = () => ({
    primary: {
        main: brandclub_colors.mainSeriesColor,
        contrastText: brandclub_colors.white,
    },
    secondary: {
        main: brandclub_colors.lightGreen,
        contrastText: brandclub_colors.mainSeriesColor,
    },
    tertiary: {
        main: brandclub_colors.blueWhite,
        contrastText: brandclub_colors.mainSeriesColor,
    },
    default: {
        // same as text color
        main: brandclub_colors.mainSeriesColor,
        contrastText: brandclub_colors.white,
    },
    info: {
        main: brandclub_colors.mainSeriesColor,
        contrastText: brandclub_colors.white,
    },
    text: {
        primary: brandclub_colors.mainSeriesColor,
        secondary: alpha(brandclub_colors.mainSeriesColor, 0.7),
        disabled: brandclub_colors.lightGray,
    },
    accent: {
        main: brandclub_colors.white,
        contrastText: brandclub_colors.mainSeriesColor,
    },
    background: {
        default: brandclub_colors.white,
        paper: brandclub_colors.white,
        alternate: brandclub_colors.blueWhite,
    },
    action: {
        disabled: brandclub_colors.lightGray,
        disabledBackground: brandclub_colors.blueWhite,
        disabledOpacity: 0.5,
    },
    divider: brandclub_colors.lightGray,
    error: {
        main: brandclub_colors.error,
        light: alpha(brandclub_colors.error, 0.05),
        contrastText: brandclub_colors.white,
    },
});
const pillPreset = {
    outOfStock: {
        main: alpha(brandclub_colors.lightGreen, 0.2),
        contrastText: brandclub_colors.mainSeriesColor,
    },
    dailyDeal: {
        main: brandclub_colors.orange_EC792E_30,
        contrastText: brandclub_colors.orange_EC792E,
    },
    purchase: {
        contrastText: brandclub_colors.magenta_A22272,
        main: brandclub_colors.magenta_A22272_30,
    },
    affiliate: {
        contrastText: brandclub_colors.teal_16949B,
        main: brandclub_colors.teal_16949B_20,
    },
    content: {
        contrastText: brandclub_colors.magenta_A22272,
        main: brandclub_colors.magenta_A22272_30,
    },
    survey: {
        contrastText: brandclub_colors.magenta_A22272,
        main: brandclub_colors.magenta_A22272_30,
    },
    default: {
        contrastText: brandclub_colors.teal_16949B,
        main: brandclub_colors.teal_16949B_20,
    },
};
const DEFAULT_FONT_FAMILY = [
    'Poppins',
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Roboto',
    '"Helvetica Neue"',
    'Arial',
    'sans-serif',
    '"Apple Color Emoji"',
    '"Segoe UI Emoji"',
    '"Segoe UI Symbol"',
].join(',');
const DEFAULT_THEME_OPTIONS = {
    palette: getDefaultThemePalette(),
    globalComponentPresets: {
        pill: pillPreset,
    },
    breakpoints: {
        values: {
            xs: SMALL_MOBILE_SCREEN_SIZE,
            sm: MOBILE_SCREEN_SIZE,
            md: TABLET_SCREEN_SIZE,
            lg: DESKTOP_SCREEN_SIZE,
            xl: XL_DESKTOP_SCREEN_SIZE,
        },
    },
    typography: {
        fontFamily: DEFAULT_FONT_FAMILY,
        /**
         * Material UI's default font size used in rem calculations.
         * https://mui.com/material-ui/customization/typography/#font-size
         */
        fontSize: 14,
        xsButton: {
            fontSize: '12px',
            fontWeight: 600,
            fontFamily: DEFAULT_FONT_FAMILY,
            lineHeight: 'inherit',
        },
        smButton: {
            fontSize: '14px',
            fontWeight: 600,
            fontFamily: DEFAULT_FONT_FAMILY,
            lineHeight: 'inherit',
        },
        button: {
            fontSize: '16px',
            fontWeight: 600,
            fontFamily: DEFAULT_FONT_FAMILY,
            lineHeight: 'inherit',
        },
        lgButton: {
            fontSize: '18px',
            fontWeight: 600,
            fontFamily: DEFAULT_FONT_FAMILY,
            lineHeight: 'inherit',
        },
    },
    components: {
        MuiButtonBase: {
            defaultProps: {
                disableRipple: true,
            },
        },
        BCSolidButton: {
            defaultProps: {
                color: 'primary',
                height: { xs: 44, md: 48 },
            },
            styleOverrides: {
                root: {
                    borderRadius: 999,
                },
            },
        },
        BCOutlineButton: {
            defaultProps: {
                color: 'primary',
                height: { xs: 44, md: 48 },
            },
            styleOverrides: {
                root: {
                    borderRadius: 999,
                    borderWidth: 1.5,
                    borderStyle: 'solid',
                },
            },
        },
        BCTextButton: {
            defaultProps: {
                color: 'primary',
                height: { xs: 44, md: 48 },
            },
            styleOverrides: {
                root: {
                    borderRadius: 999,
                },
            },
        },
        BCIconButton: {
            defaultProps: {
                color: 'primary',
                // icon buttons are square, so we use height to set the size
                height: 25,
            },
            styleOverrides: {
                root: {
                    borderRadius: 999,
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    borderRadius: '6px',
                    boxShadow: '0 1px 10px -4px #24252640',
                },
            },
        },
        MuiBackdrop: {
            styleOverrides: {
                root: {
                    backgroundColor: alpha(brandclub_colors.mainSeriesColor, 0.2),
                    transition: 'opacity 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
                    zIndex: 900,
                },
            },
        },
        MuiTypography: {
            defaultProps: {
                variantMapping: {
                    xsButton: 'button',
                    smButton: 'button',
                    lgButton: 'button',
                },
            },
        },
    },
};

export { DEFAULT_THEME_OPTIONS, getDefaultThemePalette };
//# sourceMappingURL=defaultTheme.js.map
